/**
 * Data freshness utilities for dual-source indicators.
 *
 * Thresholds (different from TTL status thresholds):
 * - Green (fresh): <= 10 seconds
 * - Orange (warning): 11-30 seconds
 * - Red (stale): > 30 seconds
 */

export type FreshnessLevel = 'fresh' | 'warning' | 'stale';

export interface FreshnessThresholds {
  /** Max age in ms for "fresh" (green) */
  fresh: number;
  /** Max age in ms for "warning" (orange) - above this is stale (red) */
  warning: number;
}

export const FRESHNESS_THRESHOLDS: FreshnessThresholds = {
  fresh: 10_000, // 10 seconds
  warning: 30_000, // 30 seconds
};

/**
 * Calculate freshness level based on age.
 * @param lastUpdateTime - Timestamp of last update, or null if no data
 * @param currentTime - Current time for comparison (defaults to Date.now())
 * @returns FreshnessLevel or null if no data
 */
export function getFreshnessLevel(
  lastUpdateTime: number | null,
  currentTime: number = Date.now()
): FreshnessLevel | null {
  if (lastUpdateTime === null) {
    return null; // No data for this source
  }

  const ageMs = currentTime - lastUpdateTime;

  if (ageMs <= FRESHNESS_THRESHOLDS.fresh) {
    return 'fresh';
  }
  if (ageMs <= FRESHNESS_THRESHOLDS.warning) {
    return 'warning';
  }
  return 'stale';
}

/**
 * Get Tailwind CSS background color class for freshness level.
 */
export function getFreshnessColorClass(level: FreshnessLevel | null): string {
  switch (level) {
    case 'fresh':
      return 'bg-green-500';
    case 'warning':
      return 'bg-orange-500';
    case 'stale':
      return 'bg-red-500';
    default:
      return 'bg-gray-300'; // N/A state
  }
}

/**
 * Calculate age in seconds from timestamp.
 * @returns Age in seconds, or null if no timestamp
 */
export function getAgeSeconds(
  lastUpdateTime: number | null,
  currentTime: number = Date.now()
): number | null {
  if (lastUpdateTime === null) return null;
  return Math.floor((currentTime - lastUpdateTime) / 1000);
}

export interface SourceFreshness {
  level: FreshnessLevel | null;
  ageSeconds: number | null;
}

export interface DualFreshness {
  v1: SourceFreshness;
  v4: SourceFreshness;
}

/**
 * Calculate dual-source freshness from timestamps.
 */
export function getDualFreshness(
  v1LastUpdateTime: number | null,
  v4LastUpdateTime: number | null,
  currentTime: number = Date.now()
): DualFreshness {
  return {
    v1: {
      level: getFreshnessLevel(v1LastUpdateTime, currentTime),
      ageSeconds: getAgeSeconds(v1LastUpdateTime, currentTime),
    },
    v4: {
      level: getFreshnessLevel(v4LastUpdateTime, currentTime),
      ageSeconds: getAgeSeconds(v4LastUpdateTime, currentTime),
    },
  };
}
